import React, { useCallback, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';

import { Image } from 'components/Image';

import styles from './ImagesGallery.module.scss';

export interface IImageBadge {
  id: string;
  badge: React.ReactNode;
}

interface IImagesGallery {
  images: Array<string>;
  imagesHigh: Array<string>;
  badges?: Array<IImageBadge>;
}

const DEFAULT_ASPECT_RATIO = '1.5/1';

const getImageGallerySettings = (images: IImagesGallery['images']) => {
  if (images.length === 1) {
    return [
      {
        aspectRatio: '3/1',
        src: images[0],
      },
    ];
  }

  if (images.length === 2) {
    return [
      {
        aspectRatio: DEFAULT_ASPECT_RATIO,
        src: images[0],
      },
      {
        aspectRatio: DEFAULT_ASPECT_RATIO,
        src: images[1],
      },
    ];
  }

  if (images.length === 3) {
    return [
      {
        aspectRatio: DEFAULT_ASPECT_RATIO,
        src: images[0],
      },
      {
        aspectRatio: '3/1',
        src: images[1],
      },
      {
        aspectRatio: '3/1',
        src: images[2],
      },
    ];
  }

  if (images.length === 4) {
    return [
      {
        aspectRatio: DEFAULT_ASPECT_RATIO,
        src: images[0],
      },
      {
        aspectRatio: '3/1',
        src: images[1],
      },
      {
        aspectRatio: DEFAULT_ASPECT_RATIO,
        src: images[2],
      },
      {
        aspectRatio: DEFAULT_ASPECT_RATIO,
        src: images[3],
      },
    ];
  }

  if (images.length >= 5) {
    return [
      {
        aspectRatio: DEFAULT_ASPECT_RATIO,
        src: images[0],
      },
      {
        aspectRatio: DEFAULT_ASPECT_RATIO,
        src: images[1],
      },
      {
        aspectRatio: DEFAULT_ASPECT_RATIO,
        src: images[2],
      },
      {
        aspectRatio: DEFAULT_ASPECT_RATIO,
        src: images[3],
      },
      {
        aspectRatio: DEFAULT_ASPECT_RATIO,
        src: images[4],
      },
    ];
  }
  return [];
};

export const ImagesGallery: React.VFC<IImagesGallery> = ({ images, imagesHigh, badges = [] }) => {
  const [isLightBoxOpen, setIsLightBoxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const imagesGallerySettings = useMemo(() => getImageGallerySettings(images), [images]);

  const LightBox = dynamic(import('../LightBox'), {
    ssr: false,
    loading: () => null, // TODO add loader if jumpy loadtime
  });

  const handleOnClickImage = useCallback((index: number) => {
    setCurrentIndex(index);
    setIsLightBoxOpen(true);
  }, []);

  return (
    <div className={styles['images-gallery-wrapper']}>
      {badges.length ? (
        <div className={`${styles['badges-wrapper']}`}>
          {badges.map((item) => (
            <React.Fragment key={item.id}>{item.badge}</React.Fragment>
          ))}
        </div>
      ) : null}
      <div
        className={`${styles['images-gallery']} ${
          styles[`images-gallery--${imagesGallerySettings.length}`]
        }`}
      >
        {imagesGallerySettings.map(({ src, aspectRatio }, index) => (
          <button
            className={styles['image-wrapper']}
            type="button"
            // eslint-disable-next-line react/no-array-index-key
            key={src + index}
            onClick={() => handleOnClickImage(index)}
          >
            <Image aspectRatio={aspectRatio} src={src} width="100%" height="100%" />
          </button>
        ))}
      </div>
      <LightBox
        images={imagesHigh}
        startIndex={currentIndex}
        isOpen={isLightBoxOpen}
        handleCloseBtn={() => setIsLightBoxOpen(false)}
      />
    </div>
  );
};
